import { FC, useMemo, useEffect, useRef, useState, useCallback } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Node,
  Edge,
  Controls,
  MiniMap,
  MarkerType,
  Background,
  BackgroundVariant,
  applyNodeChanges,
  NodeChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import styles from "@/components/company/companyHierarchy.module.css";
import CompanyHierarchyNode from "@/components/company/companyHierarchyNode";

interface CompanyTreeNode {
  id: string;
  name: string;
  children?: CompanyTreeNode[];
}

interface CompanyHierarchyProps {
  data: CompanyTreeNode;
  selectedCompanyId?: string;
  selectedSubjectId?: string;
  onNodeClick?: (nodeData: any) => void;
  disableClickForHighlightedCompany?: boolean;
}

const CompanyHierarchy: FC<CompanyHierarchyProps> = ({
  data,
  selectedCompanyId,
  selectedSubjectId,
  onNodeClick,
  disableClickForHighlightedCompany = false,
}) => {
  const reactFlowInstance = useRef<any>(null);
  const [nodesState, setNodesState] = useState<Node[]>([]);

  const calculateLayout = (
    node: CompanyTreeNode,
    level: number,
    x: number,
    y: number
  ): { nodes: Node[]; edges: Edge[]; width: number; nextX: number } => {
    const nodeWidth = 180;
    const horizontalSpacing = 75;
    const verticalSpacing = 150;

    const nodeId = node.id;
    const isHighlighted = nodeId === selectedSubjectId || nodeId === selectedCompanyId;

    const displayName = node.name?.replace(/\s*\(Firma\)\s*$/i, "") || node.name;

    const reactFlowNode: Node = {
      id: nodeId,
      type: "companyNode",
      position: { x, y },
      data: {
        label: displayName,
        subjectId: nodeId,
        isHighlighted,
      },
      selected: false,
    };

    const nodes: Node[] = [reactFlowNode];
    const edges: Edge[] = [];

    if (!node.children || node.children.length === 0) {
      // Leaf node
      return { nodes, edges, width: nodeWidth, nextX: x + nodeWidth + horizontalSpacing };
    }

    // Calculate children positions
    let currentX = x;
    let totalChildrenWidth = 0;
    const childResults: Array<{ nodes: Node[]; edges: Edge[]; width: number; nextX: number }> = [];

    // First pass: calculate all children layouts
    node.children.forEach((child) => {
      const childResult = calculateLayout(
        child,
        level + 1,
        currentX,
        y + verticalSpacing
      );
      childResults.push(childResult);
      totalChildrenWidth = Math.max(totalChildrenWidth, childResult.nextX - x);
      currentX = childResult.nextX;
    });

    // Second pass: center parent above children
    const childrenWidth = childResults[childResults.length - 1].nextX - childResults[0].nodes[0].position.x;
    const centeredX = childResults[0].nodes[0].position.x + childrenWidth / 2 - nodeWidth / 2;
    reactFlowNode.position.x = centeredX;

    // Collect all child nodes and edges
    childResults.forEach((childResult) => {
      nodes.push(...childResult.nodes);
      edges.push(...childResult.edges);
    });

    // Add edges from parent to children
    node.children.forEach((child) => {
      edges.push({
        id: `edge-${nodeId}-${child.id}`,
        source: nodeId,
        target: child.id,
        type: "smoothstep",
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: "#bdbdbd",
        },
        style: {
          stroke: "#bdbdbd",
          strokeWidth: 2,
        },
      });
    });

    return {
      nodes,
      edges,
      width: Math.max(nodeWidth, childrenWidth),
      nextX: Math.max(x + nodeWidth, childResults[childResults.length - 1].nextX) + horizontalSpacing,
    };
  };

  const { initialNodes, edges } = useMemo(() => {
    const result = calculateLayout(data, 0, 0, 0);
    const rootNode = result.nodes.find(n => n.id === data.id);
    if (rootNode) {
      const centerX = rootNode.position.x;
      const centeredNodes = result.nodes.map(node => {
        const isHighlighted = node.id === selectedSubjectId || node.id === selectedCompanyId;

        return {
          ...node,
          selected: false,
          data: {
            ...node.data,
            isHighlighted,
          },
          position: {
            x: node.position.x - centerX,
            y: node.position.y,
          },
        };
      });
      return { initialNodes: centeredNodes, edges: result.edges };
    }
    return { initialNodes: result.nodes, edges: result.edges };
  }, [data, selectedCompanyId, selectedSubjectId]);

  // Initialize nodes state
  useEffect(() => {
    setNodesState(initialNodes);
  }, [initialNodes]);

  const nodeTypes = useMemo(
    () => ({
      companyNode: (props: any) => (
        <CompanyHierarchyNode
          {...props}
          disableClickForHighlightedNode={disableClickForHighlightedCompany}
        />
      ),
    }),
    [disableClickForHighlightedCompany]
  );

  useEffect(() => {
    // Fit view after nodes are rendered
    if (reactFlowInstance.current) {
      setTimeout(() => {
        reactFlowInstance.current.fitView({
          padding: {
            top: 0.2,
            right: 0.35,
            bottom: 0.35,
            left: 0.2,
          },
          duration: 400
        });
      }, 100);
    }
  }, [initialNodes]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodesState((nds) => applyNodeChanges(changes, nds));
  }, []);

  const handleNodeClick = (_event: React.MouseEvent, node: Node) => {
    if (onNodeClick) {
      onNodeClick(node.data);
    }
  };

  return (
    <div className={styles.companyHierarchyContainer}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodesState}
          edges={edges}
          nodeTypes={nodeTypes}
          onInit={(instance) => {
            reactFlowInstance.current = instance;
          }}
          onNodesChange={onNodesChange}
          onNodeClick={handleNodeClick}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={true}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.3}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        >
          <Background variant={BackgroundVariant.Lines} gap={20} size={1} />
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              return node.data?.isHighlighted ? "#8c1ec8" : "#bdbdbd";
            }}
            style={{
              width: 150,
              height: 100,
            }}
            pannable={true}
            zoomable={true}
          />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export default CompanyHierarchy;
